import { SeverityNumber } from '@opentelemetry/api-logs';
import { telemetryService } from './telemetry-service';
import type { ILogger } from '@/domain/interfaces/i-logger';
import { parseCallerFromStack } from './caller-site';
import { resolveBundleSiteToSourceLabel } from './resolve-caller-source';

declare const __APP_VERSION__: string;

const SERVICE_NAME = 'web-app';

type LogAttributes = Record<string, string | number | boolean>;

type OtlpStringAttribute = { key: string; value: { stringValue: string } };

type QueuedLogRecord = {
  timeUnixNano: number;
  severityNumber: SeverityNumber;
  severityText: string;
  body: { stringValue: string };
  attributes: OtlpStringAttribute[];
  _callerResolve?: { mapUrl: string; line: number; column: number };
};

export class OtelLogger implements ILogger {
  private consoleEnabled: boolean = true;
  private minLogLevel: SeverityNumber = SeverityNumber.INFO;
  private initialized = false;
  private observabilityEnabled: boolean = false;
  private logQueue: QueuedLogRecord[] = [];
  private flushTimer: number | null = null;
  private environment: string = 'development';

  initialize(logLevel: string, consoleEnabled: boolean, environment?: string, observabilityEnabled: boolean = false): void {
    try {
      this.consoleEnabled = consoleEnabled;
      this.minLogLevel = this.parseLogLevel(logLevel);
      this.observabilityEnabled = observabilityEnabled;
      if (environment) {
        this.environment = environment;
      }
      this.initialized = true;

      if (this.observabilityEnabled) {
        this.flushTimer = window.setInterval(() => {
          this.flushLogs();
        }, 5000);
      }
    } catch (error) {
      console.warn('Failed to initialize logger:', error);
      this.initialized = false;
    }
    finally {
      console.log('Initializing logger with this.minLogLevel:', this.minLogLevel, 'this.consoleEnabled:', this.consoleEnabled, 'this.environment:', this.environment, 'this.observabilityEnabled:', this.observabilityEnabled);
    }
  }

  verbose(message: string, attributes?: LogAttributes): void {
    this.log(SeverityNumber.DEBUG4, 'VERBOSE', message, attributes);
  }

  debug(message: string, attributes?: LogAttributes): void {
    this.log(SeverityNumber.DEBUG, 'DEBUG', message, attributes);
  }

  info(message: string, attributes?: LogAttributes): void {
    this.log(SeverityNumber.INFO, 'INFO', message, attributes);
  }

  warn(message: string, attributes?: LogAttributes): void {
    this.log(SeverityNumber.WARN, 'WARN', message, attributes);
  }

  error(message: string, attributes?: LogAttributes): void {
    this.log(SeverityNumber.ERROR, 'ERROR', message, attributes);
  }

  async shutdown(): Promise<void> {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    await this.flushLogs();
  }

  async flush(): Promise<void> {
    await this.flushLogs();
  }

  setEnvironment(environment: string): void {
    this.environment = environment;
  }

  private async flushLogs(): Promise<void> {
    if (!this.observabilityEnabled || this.logQueue.length === 0) {
      return;
    }

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    await Promise.all(
      logsToSend.map(async (rec) => {
        const hint = rec._callerResolve;
        delete rec._callerResolve;
        if (!hint) {
          return;
        }
        const resolved = await resolveBundleSiteToSourceLabel(hint.mapUrl, hint.line, hint.column);
        if (!resolved) {
          return;
        }
        const callerAttr = rec.attributes.find((a) => a.key === 'caller');
        if (callerAttr) {
          callerAttr.value.stringValue = resolved;
        }
      })
    );

    const serviceVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0';

    try {
      await fetch(`${window.location.origin}/api/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resourceLogs: [
            {
              resource: {
                attributes: [
                  { key: 'service.name', value: { stringValue: SERVICE_NAME } },
                  { key: 'service.version', value: { stringValue: serviceVersion } },
                  { key: 'environment', value: { stringValue: this.environment } },
                ],
              },
              scopeLogs: [
                {
                  scope: {
                    name: SERVICE_NAME,
                    version: serviceVersion,
                  },
                  logRecords: logsToSend,
                },
              ],
            },
          ],
        }),
      });
    } catch (error) {
      console.warn('Failed to send logs:', error);
    }
  }

  private shouldLog(level: SeverityNumber): boolean {
    return level >= this.minLogLevel;
  }

  private log(
    severityNumber: SeverityNumber,
    severityText: string,
    message: string,
    attributes?: LogAttributes
  ): void {
    const stack = new Error().stack;
    const parsed =
      stack && typeof window !== 'undefined'
        ? parseCallerFromStack(stack, window.location.origin)
        : undefined;
    if (!this.initialized || !this.shouldLog(severityNumber)) {
      return;
    }

    if (this.consoleEnabled) {
      if (parsed?.kind === 'bundle') {
        void resolveBundleSiteToSourceLabel(parsed.mapUrl, parsed.line, parsed.column)
          .then((resolved) =>
            this.logToConsole(severityNumber, message, attributes, resolved ?? parsed.label)
          )
          .catch((err) => {
            console.warn('Failed to resolve log caller source map:', err);
            this.logToConsole(severityNumber, message, attributes, parsed.label);
          });
      } else {
        this.logToConsole(severityNumber, message, attributes, parsed?.label);
      }
    }

    const traceHeaders = telemetryService.getTraceHeaders() || {};

    const logAttributes: Record<string, unknown> = { ...attributes };
    let callerResolve: QueuedLogRecord['_callerResolve'];

    if (parsed) {
      if (parsed.kind === 'bundle') {
        logAttributes['caller.bundle'] = parsed.label;
        logAttributes['caller'] = parsed.label;
        callerResolve = { mapUrl: parsed.mapUrl, line: parsed.line, column: parsed.column };
      } else {
        logAttributes['caller'] = parsed.label;
      }
    }

    if (traceHeaders['traceparent']) {
      logAttributes['trace.traceparent'] = traceHeaders['traceparent'];
    }
    if (traceHeaders['tracestate']) {
      logAttributes['trace.tracestate'] = traceHeaders['tracestate'];
    }

    const record: QueuedLogRecord = {
      timeUnixNano: Date.now() * 1000000,
      severityNumber,
      severityText,
      body: { stringValue: message },
      attributes: Object.entries(logAttributes).map(([key, value]) => ({
        key,
        value: { stringValue: String(value) },
      })),
    };
    if (callerResolve) {
      record._callerResolve = callerResolve;
    }
    this.logQueue.push(record);

    if (this.logQueue.length >= 100) {
      this.flushLogs();
    }
  }

  private logToConsole(
    severityNumber: SeverityNumber,
    message: string,
    attributes?: LogAttributes,
    caller?: string
  ): void {
    const labeled = caller ? `[${caller}] ${message}` : message;
    const args = attributes ? [labeled, attributes] : [labeled];

    switch (severityNumber) {
      case SeverityNumber.DEBUG:
      case SeverityNumber.DEBUG2:
      case SeverityNumber.DEBUG3:
      case SeverityNumber.DEBUG4:
        console.debug(...args);
        break;
      case SeverityNumber.INFO:
      case SeverityNumber.INFO2:
      case SeverityNumber.INFO3:
      case SeverityNumber.INFO4:
        console.info(...args);
        break;
      case SeverityNumber.WARN:
      case SeverityNumber.WARN2:
      case SeverityNumber.WARN3:
      case SeverityNumber.WARN4:
        console.warn(...args);
        break;
      case SeverityNumber.ERROR:
      case SeverityNumber.ERROR2:
      case SeverityNumber.ERROR3:
      case SeverityNumber.ERROR4:
      case SeverityNumber.FATAL:
      case SeverityNumber.FATAL2:
      case SeverityNumber.FATAL3:
      case SeverityNumber.FATAL4:
        console.error(...args);
        break;
    }
  }

  private parseLogLevel(level: string): SeverityNumber {
    switch (level.toUpperCase()) {
      case 'DEBUG':
        return SeverityNumber.DEBUG;
      case 'INFO':
        return SeverityNumber.INFO;
      case 'WARN':
        return SeverityNumber.WARN;
      case 'ERROR':
        return SeverityNumber.ERROR;
      default:
        return SeverityNumber.INFO;
    }
  }
}

export const logger = new OtelLogger();
